"use client";

import { c as _c } from "react/compiler-runtime";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { enabledLocales, LOCALIZED_PAGES } from "@/constants";

/**
 * Crawlable language row for the footer.
 *
 * LanguageDropdown switches locale with window.location from a <button>, so Ahrefs
 * (and Googlebot) never see a link to /es/*, /de/* or /fr/* — all 87 non-English
 * pages were reported as orphans. These are real <a href> elements pointing at the
 * CURRENT page's locale variant when the page is in LOCALIZED_PAGES, and at the
 * locale homepage otherwise. Language names are endonyms and are never translated,
 * so no <T> wrapping here.
 */

const LANGUAGE_NAMES: Record<string, string> = {
  en: "English",
  es: "Español",
  de: "Deutsch",
  fr: "Français"
};

// Display order requested for the footer row.
const DISPLAY_ORDER = ["en", "es", "de", "fr"];
export function getLocaleHref(pathname: string, locale: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const hasLocalePrefix = segments.length > 0 && enabledLocales.includes(segments[0]);
  const withoutLocale = hasLocalePrefix ? `/${segments.slice(1).join("/")}` : pathname;

  // LOCALIZED_PAGES keys the homepage as "" and every other page as "/path" (no trailing slash)
  const normalized = withoutLocale === "/" || withoutLocale === "" ? "" : withoutLocale.replace(/\/+$/, "");
  const targetPath = LOCALIZED_PAGES.has(normalized) ? normalized : "";
  if (locale === "en") return targetPath || "/";
  return targetPath ? `/${locale}${targetPath}` : `/${locale}`;
}
export default function FooterLanguageLinks() {
  const $ = _c(5);
  const pathname = usePathname() || "/";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = DISPLAY_ORDER.filter(_temp);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const locales = t0;
  let t1;
  if ($[1] !== pathname) {
    t1 = locales.map((code_0, index) => {
      const href = getLocaleHref(pathname, code_0);
      const isCurrent = href === (pathname.replace(/\/+$/, "") || "/");
      return <span key={code_0} className="inline-flex items-center">{index > 0 && <span aria-hidden="true" className="px-1 text-[13px] text-white/25">·</span>}<a href={href} hrefLang={code_0} aria-current={isCurrent ? "page" : undefined} className={`text-[13px] font-normal leading-[20px] transition-colors hover:text-white lg:text-[14px] ${isCurrent ? "text-white/80" : "text-white/50"}`}>{LANGUAGE_NAMES[code_0] || code_0}</a></span>;
    });
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = <nav aria-label="Language" className="flex flex-wrap items-center gap-x-1 gap-y-0.5">{t1}</nav>;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function _temp(code) {
  return enabledLocales.includes(code);
}